import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';

import { EditUserForm } from '@/components/management/form/EditUserForm';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import {
  backendApiOrganizationsFindAllOptions,
  backendApiUsersFindOneOptions,
  backendApiUsersRemoveMutation,
  backendApiUsersUpdateMutation,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { useConfirmStore } from '@/stores/useConfirmStore';
import { useSnackbarStore } from '@/stores/useSnackbarStore';

export const Route = createFileRoute('/_authenticated/management/users/$userId/')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  beforeLoad: () => ({ breadcrumb: null }),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { userId } = Route.useParams();

  const { data: user } = useSuspenseQuery(
    backendApiUsersFindOneOptions({
      path: { userId },
    }),
  );
  const { data: organizations } = useSuspenseQuery(backendApiOrganizationsFindAllOptions());

  const { openSnackbar } = useSnackbarStore((state) => state.actions);
  const { openConfirm } = useConfirmStore((state) => state.actions);

  const { mutateAsync: mutateAsyncUpdate } = useMutation({
    ...backendApiUsersUpdateMutation(),
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Die Änderungen wurden erfolgreich gespeichert!',
      });
    },
    onError: () => {
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Speichern.',
      });
    },
  });

  const { mutateAsync: mutateAsyncDelete } = useMutation({
    ...backendApiUsersRemoveMutation(),
    meta: {
      invalidateQueries: false,
    },
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Der Benutzer wurde erfolgreich entfernt!',
      });
      navigate({
        to: '/management/users',
        replace: true,
      });
    },
    onError: () =>
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Löschen des Benutzers.',
      }),
  });

  return (
    <Fragment>
      <AdministrationHeader
        formId="userForm"
        label="User*in Bearbeiten"
        onDelete={async () => {
          const confirmed = await openConfirm({
            title: 'Benutzer löschen',
            message: 'Sind Sie sicher, dass Sie diesen Benutzer unwiderruflich löschen möchten?',
            confirmText: 'Löschen',
            cancelText: 'Abbrechen',
          });

          if (confirmed) {
            await mutateAsyncDelete({
              path: { userId },
            });
          }
        }}
      />
      <AdministrationBody>
        <EditUserForm
          initialValues={user}
          organizations={organizations}
          onSubmit={async (values) => {
            await mutateAsyncUpdate({ path: { userId }, body: values });
          }}
        />
      </AdministrationBody>
    </Fragment>
  );
}
